import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import { api } from '../../../lib/api';
import { Button } from '../../../components/ui/Button';
import SafeIcon from '../../../common/SafeIcon';

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    netlify_api_key: '',
    netlify_site_id: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { settings: saved } = await api.get('admin/settings');
      setSettings(prev => ({ ...prev, ...saved }));
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setLoading(false);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await api.post('admin/settings/test', settings);
      setTestResult({ success: true, siteName: result.siteName });
    } catch (err) {
      setTestResult({ success: false, error: err.message });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await api.put('admin/settings', settings);
      setMessage({ type: 'success', text: 'Settings updated successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <DashboardLayout><div className="animate-pulse p-8 text-center text-gray-500">Loading system settings...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
        <p className="text-gray-500 mt-1">Configure platform integrations and API keys.</p>
      </div>

      <form onSubmit={handleSave} className="max-w-2xl space-y-8">
        <div className="p-8 bg-indigo-50 rounded-[2.5rem] border border-indigo-100 space-y-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-indigo-600 shadow-sm">
                <SafeIcon name="Globe" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Netlify Integration</h3>
            </div>
            <button 
              type="button"
              onClick={handleTestConnection}
              disabled={testing}
              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 bg-white px-3 py-1.5 rounded-lg border border-indigo-100 shadow-sm"
            >
              {testing ? <SafeIcon name="Loader" className="animate-spin" /> : <SafeIcon name="Zap" />}
              Test Connection
            </button>
          </div>

          {testResult && (
            <div className={`p-4 rounded-xl text-xs font-medium border ${testResult.success ? 'bg-green-100 border-green-200 text-green-700' : 'bg-red-100 border-red-200 text-red-700'}`}>
              {testResult.success ? (
                <div className="flex items-center gap-2">
                  <SafeIcon name="Check" /> 
                  Connected to: <b>{testResult.siteName}</b>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <SafeIcon name="AlertTriangle" className="mt-0.5" />
                  <span>{testResult.error}</span>
                </div>
              )}
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Personal Access Token</label>
              <input 
                type="password"
                value={settings.netlify_api_key}
                onChange={e => setSettings({...settings, netlify_api_key: e.target.value})}
                placeholder="nfp_..."
                className="w-full px-4 py-3 bg-white border border-indigo-100 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">API ID (Site ID)</label>
              <input 
                type="text"
                value={settings.netlify_site_id}
                onChange={e => setSettings({...settings, netlify_site_id: e.target.value})}
                placeholder="a96c3eba-..."
                className="w-full px-4 py-3 bg-white border border-indigo-100 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
              />
              <p className="text-[10px] text-indigo-400 mt-1 ml-1">
                Found in: Site Settings &gt; General &gt; Site details &gt; API ID
              </p>
            </div>
          </div>
        </div>

        {message.text && (
          <div className={`p-4 rounded-2xl text-sm font-medium border animate-in fade-in slide-in-from-top-2 ${message.type === 'success' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-red-50 text-red-700 border-red-100'}`}>
            {message.text}
          </div>
        )}

        <Button type="submit" size="lg" className="w-full py-4 rounded-2xl shadow-xl shadow-indigo-100" disabled={saving}>
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </form>
    </DashboardLayout>
  );
}
